import type { JSX } from 'react';
import type { PricingRuleBodyInput } from '@majuscule/contracts';
import { assertNever } from '@majuscule/domain';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from './toast';
import { internalFetch, internalSend, validationDetail } from '../lib/api';
import { datetimeLocalToIso } from '../lib/format';
import {
  applicableRule,
  DEFAULT_RULE_TYPE,
  DEFAULT_UNIT,
  describeRule,
  KIND_LABELS,
  type PricingRule,
  type PricingUnit,
  ruleInEffect,
  type RuleType,
  SMS_UNIT_HINT,
  smsSettingsOf,
  UNIT_OPTION_LABELS,
  type UseCase,
  unitsForKind,
} from '../lib/use-cases';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardFoot, CardHead } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, Fields2, InputGroup } from '@/components/ui/field';
import { DateTimeInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { KvList, KvRow } from '@/components/ui/kv';
import { NativeSelect } from '@/components/ui/native-select';
import { Tag } from '@/components/ui/tag';
import {
  Cell2,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const COLUMN_COUNT = 4;

/**
 * Client-centric mirror of the use-case screen (PRD 17, "Détail client") : lists
 * the use cases that belong to this client and lets the operator set a
 * per-use-case price override. Reuses the catalog endpoints; no dedicated backend.
 */
export function ClientUseCases({ clientId }: { clientId: string }): JSX.Element {
  const useCasesQuery = useQuery({
    queryKey: ['use-cases'],
    queryFn: () => internalFetch<{ useCases: UseCase[] }>('/internal/use-cases'),
  });

  // A client only ever sees the use cases it owns (lot 15).
  const relevant = (useCasesQuery.data?.useCases ?? []).filter(
    (useCase) => useCase.ownerClientId === clientId,
  );

  const ruleQueries = useQueries({
    queries: relevant.map((useCase) => ({
      queryKey: ['pricing-rules', useCase.id],
      queryFn: () =>
        internalFetch<{ rules: PricingRule[] }>(`/internal/use-cases/${useCase.id}/pricing-rules`),
    })),
  });
  const rulesByUseCase = new Map<string, PricingRule[]>();
  relevant.forEach((useCase, index) => {
    const data = ruleQueries[index]?.data;
    if (data !== undefined) {
      rulesByUseCase.set(useCase.id, data.rules);
    }
  });

  const [editing, setEditing] = useState<UseCase | null>(null);
  const now = Date.now();
  const overrideCount = relevant.filter((useCase) =>
    (rulesByUseCase.get(useCase.id) ?? []).some((rule) => rule.clientId === clientId),
  ).length;

  return (
    <Card>
      <CardHead
        title="Cas d’usage et prix"
        description="Les cas d’usage de ce client et la surcharge de prix propre à lui."
        className="pb-4"
      />
      <Table className="min-w-[720px]">
        <TableHeader>
          <TableRow>
            <TableHead kind="name">Cas d’usage</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Prix appliqué</TableHead>
            <TableHead kind="act">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {relevant.map((useCase) => {
            const rules = rulesByUseCase.get(useCase.id);
            const applied = rules === undefined ? undefined : applicableRule(rules, clientId, now);
            const hasOverride =
              rules !== undefined && rules.some((rule) => rule.clientId === clientId);
            const sms = smsSettingsOf(useCase);
            return (
              <TableRow key={useCase.id}>
                <TableCell kind="name">
                  <Cell2
                    primary={useCase.key}
                    primaryMono
                    secondary={sms !== null ? `Expéditeur : ${sms.sender}` : undefined}
                  />
                </TableCell>
                <TableCell>
                  <Tag>{KIND_LABELS[useCase.kind]}</Tag>
                </TableCell>
                <TableCell>
                  {applied === undefined ? (
                    <span className="text-muted-foreground">…</span>
                  ) : applied === null ? (
                    <span className="text-muted-foreground">Aucune règle</span>
                  ) : (
                    <span className="flex flex-wrap items-center gap-2">
                      <span>{describeRule(applied.rule.rule)}</span>
                      {applied.source === 'override' ? (
                        <Badge variant="info">Surcharge</Badge>
                      ) : (
                        <Tag>Défaut</Tag>
                      )}
                    </span>
                  )}
                </TableCell>
                <TableCell kind="act" className="w-auto">
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      setEditing(useCase);
                    }}
                  >
                    {hasOverride ? 'Modifier la surcharge' : 'Définir une surcharge'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {useCasesQuery.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>Chargement…</TableEmptyRow>
          ) : null}
          {relevant.length === 0 && !useCasesQuery.isLoading ? (
            <TableEmptyRow colSpan={COLUMN_COUNT}>
              Aucun cas d’usage disponible pour ce client.
            </TableEmptyRow>
          ) : null}
        </TableBody>
      </Table>
      {relevant.length > 0 ? (
        <CardFoot
          start={`${relevant.length} cas d’usage, ${overrideCount} surcharge${overrideCount > 1 ? 's' : ''}`}
          end="Prix HT"
        />
      ) : null}

      {editing !== null ? (
        <OverrideDialog
          clientId={clientId}
          useCase={editing}
          rules={rulesByUseCase.get(editing.id) ?? []}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </Card>
  );
}

function OverrideDialog({
  clientId,
  useCase,
  rules,
  onClose,
}: {
  clientId: string;
  useCase: UseCase;
  rules: readonly PricingRule[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const { notify } = useToast();

  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [ruleType, setRuleType] = useState<RuleType>(DEFAULT_RULE_TYPE[useCase.kind]);
  const [coefficient, setCoefficient] = useState('');
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<PricingUnit>(DEFAULT_UNIT[useCase.kind]);
  const [formError, setFormError] = useState('');

  const units = unitsForKind(useCase.kind);
  const now = Date.now();
  const currentOverride = ruleInEffect(rules, clientId, now);
  const currentDefault = ruleInEffect(rules, null, now);

  const buildRule = (): PricingRuleBodyInput => {
    switch (ruleType) {
      case 'multiplier':
        return { type: 'multiplier', coefficient: coefficient.trim() };
      case 'fixed_per_request':
        return { type: 'fixed_per_request', amount: amount.trim() };
      case 'per_unit':
        return { type: 'per_unit', unit, amount: amount.trim() };
      default:
        return assertNever(ruleType);
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const iso = datetimeLocalToIso(effectiveFrom);
      return internalSend<PricingRule>('POST', `/internal/use-cases/${useCase.id}/pricing-rules`, {
        clientId,
        effectiveFrom: iso,
        rule: buildRule(),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pricing-rules', useCase.id] });
      notify('Surcharge de prix enregistrée');
      onClose();
    },
    onError: (error) => {
      setFormError(validationDetail(error) ?? 'Échec de l’enregistrement de la surcharge.');
    },
  });

  const submit = (): void => {
    setFormError('');
    if (datetimeLocalToIso(effectiveFrom) === null) {
      setFormError('Date d’effet requise.');
      return;
    }
    if (ruleType === 'multiplier') {
      if (!/^\d+(\.\d+)?$/.test(coefficient.trim())) {
        setFormError('Coefficient invalide (nombre décimal attendu).');
        return;
      }
    } else if (!/^\d+(\.\d{1,6})?$/.test(amount.trim())) {
      setFormError('Montant invalide (euros, ex. 0,05 → 0.05).');
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHead
          title="Surcharge de prix client"
          description={
            <>
              Cas d’usage : <span className="font-mono">{useCase.key}</span>
            </>
          }
        />
        <DialogBody>
          <KvList className="rounded-lg border border-border bg-muted px-3">
            <KvRow label="Règle par défaut">
              {currentDefault === undefined ? '—' : describeRule(currentDefault.rule)}
            </KvRow>
            <KvRow label="Surcharge actuelle">
              {currentOverride === undefined ? 'Aucune' : describeRule(currentOverride.rule)}
            </KvRow>
          </KvList>

          <Field label="Date d’effet" required>
            <DateTimeInput
              value={effectiveFrom}
              onChange={(value) => {
                setEffectiveFrom(value);
              }}
            />
          </Field>
          <Field label="Type de règle">
            <NativeSelect
              value={ruleType}
              onChange={(event) => {
                switch (event.target.value) {
                  case 'multiplier':
                  case 'fixed_per_request':
                  case 'per_unit':
                    setRuleType(event.target.value);
                    break;
                  default:
                    break;
                }
              }}
            >
              <option value="multiplier">Multiplicateur</option>
              <option value="fixed_per_request">Forfait par requête</option>
              <option value="per_unit">Par unité</option>
            </NativeSelect>
          </Field>
          {ruleType === 'multiplier' ? (
            <Field label="Coefficient" required>
              <Input
                numeric
                value={coefficient}
                onChange={(event) => {
                  setCoefficient(event.target.value);
                }}
                placeholder="1.3"
                inputMode="decimal"
              />
            </Field>
          ) : null}
          {ruleType === 'fixed_per_request' ? (
            <Field label="Montant" required>
              <InputGroup suffix="€">
                <Input
                  numeric
                  value={amount}
                  onChange={(event) => {
                    setAmount(event.target.value);
                  }}
                  placeholder="0.05"
                  inputMode="decimal"
                />
              </InputGroup>
            </Field>
          ) : null}
          {ruleType === 'per_unit' ? (
            <Fields2>
              <Field label="Unité">
                <NativeSelect
                  value={unit}
                  onChange={(event) => {
                    const next = units.find((option) => option === event.target.value);
                    if (next !== undefined) {
                      setUnit(next);
                    }
                  }}
                >
                  {units.map((option) => (
                    <option key={option} value={option}>
                      {UNIT_OPTION_LABELS[option]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Montant" required hint={unit === 'sms' ? SMS_UNIT_HINT : undefined}>
                <InputGroup suffix="€">
                  <Input
                    numeric
                    value={amount}
                    onChange={(event) => {
                      setAmount(event.target.value);
                    }}
                    placeholder="0.05"
                    inputMode="decimal"
                  />
                </InputGroup>
              </Field>
            </Fields2>
          ) : null}
          {formError.length > 0 ? <Callout variant="destructive">{formError}</Callout> : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="button" size="lg" onClick={submit} disabled={save.isPending}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
